// Pop-out composer of the single-column layout, after Mastodon 5.0: a window at the
// bottom centre (full screen on narrow screens) that can be minimized to a bar
// without losing the post in progress. Replies, mentions, edits and drafts open it
// through the compose reducer (OPENS_POPOUT); "New post" opens it directly.

import { useCallback, useEffect, useRef } from "react";

import { defineMessages, useIntl } from "react-intl";

import classNames from "classnames";

import {
  closeComposePopout,
  enableComposePopout,
  minimizeComposePopout,
  openComposePopout,
  resetCompose,
  saveComposeDraft,
} from "flavours/glitch/actions/compose";
import { openModal } from "flavours/glitch/actions/modal";
import { Icon } from "flavours/glitch/components/icon";
import ComposeFormContainer from "flavours/glitch/features/compose/containers/compose_form_container";
import { useAppDispatch, useAppSelector } from "flavours/glitch/store";

const messages = defineMessages({
  newPost: { id: "compose_popout.title.new", defaultMessage: "New post" },
  reply: { id: "compose_popout.title.reply", defaultMessage: "Reply" },
  edit: { id: "compose_popout.title.edit", defaultMessage: "Edit post" },
  minimize: { id: "compose_popout.minimize", defaultMessage: "Minimize" },
  expand: { id: "compose_popout.expand", defaultMessage: "Continue writing" },
  close: { id: "compose_popout.close", defaultMessage: "Close" },
  discardMessage: { id: "compose_popout.discard.message", defaultMessage: "Discard this post?" },
  discardConfirm: { id: "compose_popout.discard.confirm", defaultMessage: "Discard" },
  saveDraft: { id: "compose_popout.discard.save_draft", defaultMessage: "Save as draft" },
});

const hasContent = compose => (
  compose.get("text", "").trim().length > 0
  || (compose.get("spoiler") && compose.get("spoiler_text", "").trim().length > 0)
  || compose.get("media_attachments").size > 0
  || !!compose.get("poll")
);

const excerpt = text => {
  const line = text.trim().replace(/\s+/g, " ");
  return line.length > 80 ? `${line.slice(0, 80)}…` : line;
};

export const ComposePopout = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const state = useAppSelector(s => s.getIn(["compose", "popout"]));
  const compose = useAppSelector(s => s.get("compose"));
  const rootRef = useRef(null);
  const expandRef = useRef(null);

  useEffect(() => {
    dispatch(enableComposePopout(true));
    return () => dispatch(enableComposePopout(false));
  }, [dispatch]);

  // Focus the text field when the window opens, the bar when it is minimized.
  useEffect(() => {
    if (state === "open") {
      const textarea = rootRef.current?.querySelector(".compose-form textarea");
      textarea?.focus({ preventScroll: true });
    } else if (state === "minimized") {
      expandRef.current?.focus({ preventScroll: true });
    }
  }, [state]);

  const empty = !hasContent(compose);
  const editing = compose.get("id") !== null;

  const handleMinimize = useCallback(() => dispatch(minimizeComposePopout()), [dispatch]);
  const handleExpand = useCallback(() => dispatch(openComposePopout()), [dispatch]);

  const discard = useCallback(() => {
    dispatch(resetCompose());
    dispatch(closeComposePopout());
  }, [dispatch]);

  const handleClose = useCallback(() => {
    if (empty && !editing) {
      dispatch(resetCompose());
      dispatch(closeComposePopout());
      return;
    }

    dispatch(openModal({
      modalType: "CONFIRM",
      modalProps: {
        message: intl.formatMessage(messages.discardMessage),
        confirm: intl.formatMessage(messages.discardConfirm),
        onConfirm: discard,
        ...(!editing && !empty ? {
          secondary: intl.formatMessage(messages.saveDraft),
          onSecondary: () => {
            dispatch(saveComposeDraft());
            discard();
          },
        } : {}),
      },
    }));
  }, [dispatch, intl, empty, editing, discard]);

  const handleKeyDown = useCallback((e) => {
    // Autosuggest and other menus handle Escape themselves and stop it there.
    if (e.key === "Escape" && !e.defaultPrevented) {
      e.preventDefault();
      dispatch(minimizeComposePopout());
    }
  }, [dispatch]);

  if (state === "closed") {
    return null;
  }

  let title = intl.formatMessage(messages.newPost);
  if (editing) {
    title = intl.formatMessage(messages.edit);
  } else if (compose.get("in_reply_to")) {
    title = intl.formatMessage(messages.reply);
  }

  if (state === "minimized") {
    const text = excerpt(compose.get("text", ""));

    return (
      <div className='compose-popout compose-popout--minimized'>
        <button type='button' className='compose-popout__bar' onClick={handleExpand} ref={expandRef} aria-label={intl.formatMessage(messages.expand)}>
          <Icon id='pencil-simple-line' />
          <span className='compose-popout__bar-text'>
            <strong>{title}</strong>
            {text && <span>{text}</span>}
          </span>
          <Icon id='caret-down' className='compose-popout__expand-icon' />
        </button>
        <button type='button' className='compose-popout__icon-button' onClick={handleClose} title={intl.formatMessage(messages.close)} aria-label={intl.formatMessage(messages.close)}>
          <Icon id='x' />
        </button>
      </div>
    );
  }

  return (
    // Escape anywhere in the window minimizes it, like closing a dialog.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div className={classNames("compose-popout", "compose-popout--open")} role='dialog' aria-label={title} ref={rootRef} onKeyDown={handleKeyDown}>
      <div className='compose-popout__header'>
        <h2>{title}</h2>
        <button type='button' className='compose-popout__icon-button' onClick={handleMinimize} title={intl.formatMessage(messages.minimize)} aria-label={intl.formatMessage(messages.minimize)}>
          <Icon id='caret-down' />
        </button>
        <button type='button' className='compose-popout__icon-button' onClick={handleClose} title={intl.formatMessage(messages.close)} aria-label={intl.formatMessage(messages.close)}>
          <Icon id='x' />
        </button>
      </div>
      <div className='compose-popout__body'>
        <ComposeFormContainer singleColumn />
      </div>
    </div>
  );
};
